/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { Injectable } from "@angular/core";
import { catchError, map, Observable, of } from "rxjs";
import { BarChart } from "../../models/http/charts/bar-chart/bar-chart.model";
import { LineChart } from "../../models/http/charts/line-chart/line-chart.model";
import { PieChart } from "../../models/http/charts/pie-chart/pie-chart.model";
import { AdvancedFilters } from "../../models/http/dashboard/advanced-filters.model";
import { ChartResponse } from "../../models/ui/charts.model";
import { CommunicationService } from "../communication.service";

import { HttpMethods } from "../../models/ui/http-methods.model";
import { ENDPOINT_GROUPS } from "../../utils/apis/apis.constants";

@Injectable({
	providedIn: "root"
})
export class DashboardComponentEmissionsHttpService {
	private _dashboardComponentEmissionsOperations =
		ENDPOINT_GROUPS.dashboardComponentEmissions.operations;

	constructor(private _communicationService: CommunicationService) {}

	/**
	 * Retrieves the total CO2 emissions data for a line chart.
	 *
	 * @param filters - The AdvancedFilters request body.
	 * @returns An Observable that emits the line chart data for the total CO2 emissions.
	 */
	public getTotalCo2Emissions(filters: AdvancedFilters): Observable<ChartResponse<LineChart>> {
		return this._communicationService
			.callService(
				this._dashboardComponentEmissionsOperations.getTotalCo2Emissions.service,
				HttpMethods.POST,
				{},
				filters
			)
			.pipe(
				map((response: LineChart) => ({ chart: response, hasError: false })),
				catchError(() => of({ chart: {} as LineChart, hasError: true }))
			);
	}

	/**
	 * Fetches the embodied emissions bar chart data.
	 *
	 * @param filters - The AdvancedFilters request body.
	 * @returns An Observable that emits the BarChart data.
	 */
	public getTop10Emissions(filters: AdvancedFilters): Observable<ChartResponse<BarChart>> {
		return this._communicationService
			.callService(
				this._dashboardComponentEmissionsOperations.getTop10Emissions.service,
				HttpMethods.POST,
				{},
				filters
			)
			.pipe(
				map(response => ({ chart: response, hasError: false })),
				catchError(() => of({ chart: {} as BarChart, hasError: true }))
			);
	}

	/**
	 * Fetches the pie chart data for resource emissions.
	 *
	 * @param filters - The AdvancedFilters request body.
	 * @returns An Observable that emits the PieChart data.
	 */
	public getResourcesEmissions(filters: AdvancedFilters): Observable<ChartResponse<PieChart>> {
		return this._communicationService
			.callService(
				this._dashboardComponentEmissionsOperations.getResourcesEmissions.service,
				HttpMethods.POST,
				{},
				filters
			)
			.pipe(
				map(response => ({ chart: response, hasError: false })),
				catchError(() => of({ chart: {} as PieChart, hasError: true }))
			);
	}
}
